import type { AttachmentRequirementDisplay } from "@/services/shared/attachment-requirement.types";

export const AttachmentPolicyMode = {
	None: "none",
	Minimum: "minimum",
	Named: "named",
} as const;

export type AttachmentPolicyMode =
	(typeof AttachmentPolicyMode)[keyof typeof AttachmentPolicyMode];

export interface AttachmentFileRef {
	id: string;
	createdAt?: string | null;
	requirementTagId?: string | null;
}

export function attachmentPolicyMode(
	requirement: AttachmentRequirementDisplay | null | undefined,
): AttachmentPolicyMode {
	if (!requirement) return AttachmentPolicyMode.None;
	if (requirement.minimumTotalAttachments > 0) {
		return AttachmentPolicyMode.Minimum;
	}
	if ((requirement.tags ?? []).length > 0) return AttachmentPolicyMode.Named;
	return AttachmentPolicyMode.None;
}

/** What the admin Attachments row shows for a type's requirement. */
export const RequiredAttachmentsFieldPresentation = {
	/** No requirement row yet — prompt, no value. */
	Unset: "unset",
	/** Saved requirement with minimum 0 and no tags — value "None". */
	None: "none",
	Minimum: "minimum",
	Named: "named",
} as const;

export type RequiredAttachmentsFieldPresentation =
	(typeof RequiredAttachmentsFieldPresentation)[keyof typeof RequiredAttachmentsFieldPresentation];

export function requiredAttachmentsFieldPresentation(
	requirement: AttachmentRequirementDisplay | null | undefined,
): RequiredAttachmentsFieldPresentation {
	if (requirement == null) return RequiredAttachmentsFieldPresentation.Unset;
	if (requirement.minimumTotalAttachments > 0) {
		return RequiredAttachmentsFieldPresentation.Minimum;
	}
	if ((requirement.tags ?? []).length > 0) {
		return RequiredAttachmentsFieldPresentation.Named;
	}
	return RequiredAttachmentsFieldPresentation.None;
}

/**
 * Uses the entity pin when it matches the type's current requirement.
 * A pin with an empty policy (minimum 0 and no tags) is None.
 */
export function resolveAttachmentPolicy(
	pinnedRequirementId: string | null | undefined,
	requirement: AttachmentRequirementDisplay | null | undefined,
): AttachmentRequirementDisplay | null {
	if (!pinnedRequirementId || !requirement) return null;
	if (requirement.id !== pinnedRequirementId) return null;
	if (attachmentPolicyMode(requirement) === AttachmentPolicyMode.None) {
		return null;
	}
	return requirement;
}

function compareFiles(a: AttachmentFileRef, b: AttachmentFileRef): number {
	const aTime = a.createdAt ?? "";
	const bTime = b.createdAt ?? "";
	if (aTime < bTime) return -1;
	if (aTime > bTime) return 1;
	if (a.id < b.id) return -1;
	if (a.id > b.id) return 1;
	return 0;
}

export function partitionMinimumFiles<T extends AttachmentFileRef>(
	files: T[],
	minimumTotal: number,
): { required: T[]; additional: T[] } {
	const ordered = [...files].sort(compareFiles);
	const requiredCount = Math.max(0, minimumTotal);
	return {
		required: ordered.slice(0, requiredCount),
		additional: ordered.slice(requiredCount),
	};
}

export interface NamedAttachmentSection<T extends AttachmentFileRef> {
	tagId: string;
	name: string;
	description?: string | null;
	allowedKind: string;
	minimumCount: number;
	files: T[];
	/** Capped at minimumCount for the progress label. */
	satisfied: number;
}

export function partitionNamedFiles<T extends AttachmentFileRef>(
	files: T[],
	tags: AttachmentRequirementDisplay["tags"],
): { sections: NamedAttachmentSection<T>[]; additional: T[] } {
	const orderedTags = [...tags].sort((a, b) => a.sortOrder - b.sortOrder);
	const tagIds = new Set(orderedTags.map((tag) => tag.id));
	const sections = orderedTags.map((tag) => {
		const tagged = files
			.filter((file) => file.requirementTagId === tag.id)
			.sort(compareFiles);
		const minimumCount = Math.max(0, tag.minimumCount);
		return {
			tagId: tag.id,
			name: tag.name,
			description: tag.description,
			allowedKind: tag.allowedKind,
			minimumCount,
			files: tagged,
			satisfied: Math.min(tagged.length, minimumCount),
		};
	});
	const additional = files
		.filter(
			(file) =>
				!file.requirementTagId || !tagIds.has(file.requirementTagId),
		)
		.sort(compareFiles);
	return { sections, additional };
}

export const ATTACHMENT_PHOTO_ACCEPT =
	".png,.jpg,.jpeg,image/png,image/jpeg";

export const ATTACHMENT_FILE_ACCEPT =
	".pdf,.txt,.csv,.docx,.xlsx,application/pdf,text/plain,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function acceptForAllowedKind(allowedKind: string): string | undefined {
	if (allowedKind === "photo") return ATTACHMENT_PHOTO_ACCEPT;
	if (allowedKind === "file") return ATTACHMENT_FILE_ACCEPT;
	return undefined;
}
